import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ReceiptActions({ payment, onChanged, iconOnly = false }) {
  const { user } = useAuth();
  const [editOpen, setEditOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  if (user?.role !== "admin") return null;

  const openEdit = () => {
    setForm({
      fee_month: payment.fee_month || "",
      amount_paid: String(payment.amount_paid ?? ""),
      discount: String(payment.discount ?? 0),
      payment_mode: payment.payment_mode || "Cash",
      remarks: payment.remarks || "",
    });
    setEditOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/payments/${payment.id}`, {
        fee_month: form.fee_month,
        amount_paid: Number(form.amount_paid || 0),
        discount: Number(form.discount || 0),
        payment_mode: form.payment_mode,
        remarks: form.remarks,
      });
      toast.success("Receipt updated");
      setEditOpen(false);
      onChanged?.();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/payments/${payment.id}`);
      toast.success("Receipt deleted");
      setDelOpen(false);
      onChanged?.();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <>
      <Button size="sm" variant="ghost" className="text-indigo-600" onClick={openEdit} title="Edit receipt" data-testid={`edit-${payment.receipt_number}`}>
        <Pencil size={14} />{!iconOnly && <span className="ml-1">Edit</span>}
      </Button>
      <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => setDelOpen(true)} title="Delete receipt" data-testid={`delete-${payment.receipt_number}`}>
        <Trash2 size={14} />{!iconOnly && <span className="ml-1">Delete</span>}
      </Button>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-receipt-dialog">
          <DialogHeader><DialogTitle>Edit Receipt {payment.receipt_number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Fee Month</Label><Input value={form.fee_month} onChange={set("fee_month")} data-testid="edit-fee-month" /></div>
            <div><Label>Amount Paid</Label><Input type="number" value={form.amount_paid} onChange={set("amount_paid")} data-testid="edit-amount" /></div>
            <div><Label>Discount</Label><Input type="number" value={form.discount} onChange={set("discount")} data-testid="edit-discount" /></div>
            <div><Label>Payment Mode</Label>
              <Select value={form.payment_mode} onValueChange={(v) => setForm((f) => ({ ...f, payment_mode: v }))}>
                <SelectTrigger data-testid="edit-mode"><SelectValue /></SelectTrigger>
                <SelectContent>{["Cash", "UPI", "Bank Account", "Cheque", "Other"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Remarks</Label><Input value={form.remarks} onChange={set("remarks")} data-testid="edit-remarks" /></div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving} data-testid="save-edit-receipt">{saving ? <Loader2 className="animate-spin" /> : "Save Changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent data-testid="delete-receipt-dialog">
          <DialogHeader><DialogTitle>Delete Receipt {payment.receipt_number}?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">This permanently removes the receipt from records. This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={doDelete} disabled={saving} data-testid="confirm-delete-receipt">{saving ? <Loader2 className="animate-spin" /> : "Delete Permanently"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
